import { Trash } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import {
  CardBody,
  Col,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
} from 'reactstrap'

import AmountInput from 'components/AmountInput'
import CustomButton from 'components/Buttons/CustomButton'
import useAutoFetchPublicVersion from 'hooks/useAutoFetchPublicVersion'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import Select from 'react-select'
import SelectInputWithTestId from 'components/PMBSelect/SelectInputWithTestId'
import { getComodities } from '../purchaseCommodityAction'
import { removeItem, setOrdersPayload } from '../purchaseCommoditySlice'

const Item = ({ fieldNum, idx, isProductionUnit }) => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const autoFetchPublicVersion = useAutoFetchPublicVersion()

  const { orders, addItemCount, comodities } = useSelector(
    (state) => state.purchaseCommmodity,
  )

  const [listComodities, setListComodities] = useState([])

  const handleChange = (name, e, i) => {
    let key = Object.prototype.hasOwnProperty.call(e, 'sku') ? 'nameItem' : name
    let value = key === 'nameItem' ? e.label : e
    let imageUrl =
      key === 'nameItem' ? (e.img ?? e?.linkFoto) : orders.item[i].imageUrl
    let sku = key === 'nameItem' ? (e.sku ?? e?.SKU) : orders.item[i].SKU
    let itemId = key === 'nameItem' ? (e?.value ?? e.id) : orders.item[i].itemId
    let newData = [...orders.item]

    // helper: parse localized numeric input into JS number string (dot decimal)
    const parseLocaleNumber = (val) => {
      if (val === null || val === undefined) return ''
      let s = String(val).trim()
      if (s === '') return ''

      // If contains both separators
      if (s.includes('.') && s.includes(',')) {
        // if last comma after last dot -> assume dot is thousands, comma is decimal
        if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
          s = s.replace(/\./g, '').replace(/,/g, '.')
        } else {
          // comma thousands, dot decimal
          s = s.replace(/,/g, '')
        }
      } else if (s.includes(',')) {
        // only comma -> treat as decimal
        s = s.replace(/,/g, '.')
      } else if (s.includes('.')) {
        const parts = s.split('.')
        if (parts.length > 2) {
          s = parts.join('')
        } else if (parts.length === 2) {
          if (
            parts[1].length === 3 &&
            /^\d+$/.test(parts[0]) &&
            /^\d+$/.test(parts[1])
          ) {
            s = parts.join('')
          }
        }
        // otherwise keep as-is (dot as decimal)
      } else {
        // no separators
      }

      // remove any non-digit except leading - and decimal point
      s = s.replace(/[^0-9.-]/g, '')
      return s
    }

    newData[i] = {
      ...newData[i],
      orderItemId: addItemCount[i],
      [key]: value,
      SKU: sku,
      itemId: itemId,
      imageUrl: imageUrl,
      totalPrice:
        key === 'price' && orders.item[i]?.amount
          ? Number(parseLocaleNumber(value || '')).valueOf() *
            Number(parseLocaleNumber(newData[i]?.amount || '')).valueOf()
          : key === 'amount' && orders.item[i]?.price
            ? Number(parseLocaleNumber(newData[i]?.price || '')).valueOf() *
              Number(parseLocaleNumber(value || '')).valueOf()
            : newData[i]?.totalPrice,
    }
    dispatch(setOrdersPayload(newData))
  }

  const handleChangeInputSeparator = (data, name, idx) => {
    if (data !== orders?.item?.[idx]?.[name]) {
      handleChange(name, data, idx)
    }
  }

  const handleLastPick = (e) => {
    const newListComodities = [...listComodities]

    const updatedOptions = newListComodities[0]?.options?.filter(
      (item) => item.id !== e.id,
    )

    updatedOptions.unshift(e)

    if (updatedOptions.length > 5) {
      const removedItem = updatedOptions.pop()

      newListComodities[1] = {
        ...newListComodities[1],
        options: [...newListComodities[1].options, removedItem],
      }
    }

    newListComodities[0] = {
      ...newListComodities[0],
      options: updatedOptions,
    }

    newListComodities[1] = {
      ...newListComodities[1],
      options: newListComodities[1].options.filter((item) => item.id !== e.id),
    }

    localStorage.setItem(
      'lastPickedCommoditiesBuy',
      JSON.stringify(updatedOptions),
    )

    setListComodities(newListComodities)
  }

  useEffect(() => {
    if (comodities?.length < 1) {
      dispatch(getComodities({ isProductionUnit: isProductionUnit }))
    }
    setListComodities(comodities)
  }, [comodities])

  // Auto-select komoditas when prefill data has nameItem but no selection yet
  useEffect(() => {
    const currentItem = orders?.item?.[idx]

    // Only process if item has nameItem but no itemId selected yet
    if (!currentItem?.nameItem || currentItem?.itemId) {
      return
    }

    // Skip if listComodities not ready
    if (!listComodities?.length) {
      return
    }

    // Try to find matching komoditas in listComodities
    let matchedOption = null

    // Search in all commodity groups
    for (const group of listComodities) {
      if (!group.options) continue

      matchedOption = group.options.find((opt) => {
        const optName =
          opt.label?.toLowerCase() || opt.nameItem?.toLowerCase() || ''
        const currentName = currentItem.nameItem?.toLowerCase() || ''
        return optName === currentName
      })

      if (matchedOption) break
    }

    // If found a match, auto-select it
    if (matchedOption) {
      handleChange('nameItem', matchedOption, idx)
      if (!isProductionUnit) {
        handleLastPick(matchedOption)
      }
    }
  }, [
    listComodities,
    idx,
    orders?.item?.[idx]?.nameItem,
    orders?.item?.[idx]?.itemId,
    isProductionUnit,
  ])

  return (
    <CardBody className={isProductionUnit ? 'p-0' : ''}>
      <FormGroup data-testid={`purchase-form-item-select-${idx}`}>
        <Label for="nameItem">
          Barang{isProductionUnit ? ` ${idx + 1}` : ''}
        </Label>
        <Select
          id="nameItem"
          name="nameItem"
          placeholder="Pilih barang"
          options={listComodities}
          onChange={(e) => {
            handleChange('nameItem', e, idx)
            if (!isProductionUnit) {
              handleLastPick(e)
            }
          }}
          value={
            listComodities[0]?.options?.find(
              (i) => i.value === orders?.item?.[idx]?.itemId,
            ) ||
            listComodities[1]?.options?.find(
              (i) => i.value === orders?.item?.[idx]?.itemId,
            )
          }
          defaultValue={
            listComodities[0]?.options?.find(
              (i) => i.value === orders?.item?.[idx]?.itemId,
            ) ||
            listComodities[1]?.options?.find(
              (i) => i.value === orders?.item?.[idx]?.itemId,
            )
          }
          components={{ Input: SelectInputWithTestId }}
          data-testid={`purchase-form-item-select-${idx}`}
        />
      </FormGroup>
      {/* section 1 */}
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label for="amount">
              {isProductionUnit ? 'Jumlah Barang' : 'Berat'}
            </Label>
            <AmountInput
              name="amount"
              idx={idx}
              cb={handleChangeInputSeparator}
              placeholder="0"
              value={orders?.item?.[idx]?.amount ?? ''}
              preserveRaw={true}
              maxFractionDigits={6}
              testId={`purchase-form-qty-${idx}`}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup data-testid={`purchase-form-unit-select-${idx}`}>
            <Label for="unit">Satuan</Label>
            <Select
              id="unit"
              name="unit"
              placeholder="Pilih unit"
              options={autoFetchPublicVersion?.unitArray ?? []}
              onChange={(e) => handleChange('unit', e, idx)}
              value={orders?.item?.[idx]?.unit}
              defaultValue={orders?.item[idx]?.unit}
              components={{ Input: SelectInputWithTestId }}
              data-testid={`purchase-form-unit-select-${idx}`}
            />
          </FormGroup>
        </Col>
        {isProductionUnit && (
          <div className="">
            {fieldNum !== 1 && (
              <CustomButton
                color="danger"
                outline
                className="float-end"
                onClick={() => dispatch(removeItem(fieldNum))}
                size="sm"
                testId={`purchase-form-remove-item-${idx}`}
              >
                <Trash size={16} /> Hapus
              </CustomButton>
            )}
          </div>
        )}
      </Row>
      {/* section 2 */}
      {!isProductionUnit && (
        <>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="price">Harga per satuan</Label>
                <InputGroup>
                  <InputGroupText>Rp</InputGroupText>
                  <AmountInput
                    name="price"
                    idx={idx}
                    cb={handleChangeInputSeparator}
                    placeholder="0"
                    dotOnly
                    value={orders?.item?.[idx]?.price ?? ''}
                    testId={`purchase-form-price-${idx}`}
                  />
                </InputGroup>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="total">Total harga</Label>
                <InputGroup>
                  <InputGroupText>Rp</InputGroupText>
                  <Input
                    id="total"
                    name="total"
                    type="text"
                    disabled
                    placeholder={intl.formatNumber(
                      isNaN(orders?.item[idx]?.totalPrice)
                        ? 0
                        : orders?.item[idx]?.totalPrice,
                      {
                        useGrouping: 'always',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      },
                    )}
                  />
                </InputGroup>
              </FormGroup>
            </Col>
            <div className="">
              {fieldNum !== 1 && (
                <CustomButton
                  color="danger"
                  outline
                  className="float-end"
                  onClick={() => dispatch(removeItem(fieldNum))}
                  size="sm"
                  testId={`purchase-form-remove-item-${idx}`}
                >
                  <Trash size={16} /> Hapus
                </CustomButton>
              )}
            </div>
          </Row>
        </>
      )}
      {addItemCount.length > 1 &&
        fieldNum !== addItemCount[addItemCount.length - 1] && (
          <hr style={{ height: '2px' }} />
        )}
    </CardBody>
  )
}

export default Item
